import { Button, Card, faDigits } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type ForecastHolidayRow } from "../../api.js";
import { tehranToday } from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";

export function HolidaysTab() {
  const [businessDate, setBusinessDate] = useState(() => tehranToday());
  const [title, setTitle] = useState("");

  const holidays = useQuery({
    queryKey: ["forecast", "holidays"],
    queryFn: () => api<ForecastHolidayRow[]>("/forecast/holidays"),
  });

  const addAction = useAction(async () => {
    if (!businessDate || !title.trim()) return;
    await api("/forecast/holidays", {
      body: { businessDate, title: title.trim() },
    });
    setTitle("");
  }, [["forecast", "holidays"]]);

  const removeAction = useAction(
    async (id: string) => {
      await api(`/forecast/holidays/${id}`, {
        method: "DELETE",
      });
    },
    [["forecast", "holidays"]],
  );

  const list = holidays.data ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Add Holiday Form */}
      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            addAction.run({});
          }}
          className="stack"
          style={{ gap: "var(--s3)" }}
        >
          <h3 className="t-title" style={{ fontSize: "1.1rem" }}>
            ثبت روز تعطیل تقویمی جدید
          </h3>
          <p className="ink2" style={{ margin: 0, fontSize: "0.9rem" }}>
            روزهای ثبت‌شده به عنوان تعطیل رسمی در الگوریتم پیش‌بینی سفارش مستثنی شده و جزو روزهای عادی کاری
            مقایسه نمی‌شوند.
          </p>

          <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "flex-end" }}>
            <div className="field">
              <label htmlFor="holiday-date">تاریخ تعطیلی</label>
              <input
                id="holiday-date"
                type="date"
                className="input"
                value={businessDate}
                onChange={(e) => {
                  setBusinessDate(e.target.value);
                }}
                required
              />
            </div>

            <div className="field flex-1">
              <label htmlFor="holiday-title">عنوان یا مناسبت</label>
              <input
                id="holiday-title"
                type="text"
                className="input"
                placeholder="مثلاً: عید فطر، شهادت، تعطیل رسمی..."
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                }}
                required
              />
            </div>

            <Button type="submit" disabled={addAction.busy || !title.trim()}>
              {addAction.busy ? "در حال ثبت..." : "افزودن روز تعطیل"}
            </Button>
          </div>

          <ErrorLine error={addAction.error || removeAction.error} />
        </form>
      </Card>

      {/* Holidays List */}
      <Card>
        <table className="table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>تاریخ تعطیلی</th>
              <th>مناسبت</th>
              <th>تاریخ ثبت در سامانه</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {holidays.isLoading && (
              <tr>
                <td colSpan={4} style={{ textAlign: "center" }}>
                  در حال دریافت فهرست روزهای تعطیل...
                </td>
              </tr>
            )}
            {!holidays.isLoading && list.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", color: "var(--muted)" }}>
                  هنوز هیچ روز تعطیلی ثبت نشده است.
                </td>
              </tr>
            ) : (
              list.map((h) => (
                <tr key={h.id}>
                  <td>
                    <strong className="num">{faDigits(h.businessDate)}</strong>
                  </td>
                  <td>{h.title}</td>
                  <td className="hint ink2 num">{faDigits(h.createdAt.slice(0, 10))}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-subtle"
                      style={{ color: "var(--coral-ink)" }}
                      disabled={removeAction.busy}
                      onClick={() => {
                        if (window.confirm(`آیا از حذف روز تعطیل «${h.title}» مطمئنید؟`)) {
                          removeAction.run(h.id);
                        }
                      }}
                    >
                      حذف
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
